import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { ButtonLink } from '../ui/ButtonLink';
import { heroSlides } from '../../data/heroSlides';
import { EASE_OUT } from '../../utils/motion';
const INTERVAL_MS = 6500;
export function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [pageHidden, setPageHidden] = useState(false);
  const reduceMotion = useReducedMotion();
  const count = heroSlides.length;
  const paused = hovered || focusWithin || userPaused || pageHidden || !!reduceMotion;
  const slide = heroSlides[index];
  const go = useCallback((delta: number) => setIndex(current => (current + delta + count) % count), [count]);
  useEffect(() => {
    const onVisibility = () => setPageHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => go(1), INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [index, paused, go]);
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') go(1);
    if (event.key === 'ArrowLeft') go(-1);
  };
  return <section aria-roledescription="carousel" aria-label="Liftoff Investments highlights" className="relative h-[620px] overflow-hidden bg-charcoal-950 md:h-[600px] lg:h-[650px]" onPointerEnter={e => e.pointerType === 'mouse' && setHovered(true)} onPointerLeave={() => setHovered(false)} onFocus={() => setFocusWithin(true)} onBlur={e => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocusWithin(false);
  }} onKeyDown={onKeyDown} data-id="element-161" data-mp="1kuq00d0w5ot5">
      <h1 className="sr-only" data-id="element-162" data-mp="3urdpndl9crpt">Liftoff Investments: civil engineering and construction in Botswana</h1>

      {heroSlides.map((item, i) => {
      const active = i === index;
      return <motion.img key={item.id} src={item.image.src} alt="" aria-hidden="true" loading={i === 0 ? 'eager' : 'lazy'} decoding="async" initial={false} animate={{
        opacity: active ? 1 : 0,
        scale: active && !reduceMotion ? 1 : 1.06
      }} transition={{
        opacity: {
          duration: 0.3,
          ease: EASE_OUT
        },
        scale: active ? {
          duration: 7,
          ease: 'linear'
        } : {
          duration: 0
        }
      }} className="absolute inset-0 h-full w-full object-cover object-[72%_center] md:object-center" data-id="element-163" data-mp="8k5oknhdf6zl" data-mpi={i} />;
    })}
      <div className="absolute inset-0 bg-charcoal-950/60 md:bg-charcoal-950/25" aria-hidden="true" data-id="element-164" data-mp="2t31sl8a8s8se" />

      <div className="relative mx-auto flex h-full max-w-site flex-col justify-center px-5 pb-24 md:px-8" data-id="element-165" data-mp="18lahm4olhsrj">
        <AnimatePresence mode="wait" initial={false} data-id="element-166" data-mp="1dhr8h6n6il8b">
          <motion.div key={slide.id} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${count}: ${slide.label}`} initial={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: EASE_OUT
        }} className="max-w-[720px]" data-id="element-167" data-mp="yufsmsjysf2e">
            <p className="flex items-center gap-3 font-display text-[15px] font-semibold uppercase tracking-[0.2em] text-steel-100" data-id="element-168" data-mp="fwv45ml43iib">
              <span className="h-[3px] w-10 bg-brand-red" aria-hidden="true" data-id="element-169" data-mp="kw7zkvq1txwi" />
              {slide.label}
            </p>
            <h2 className="mt-5 font-display text-[46px] font-extrabold uppercase leading-[0.9] text-white sm:text-[60px] lg:text-[78px]" data-id="element-170" data-mp="2f8xwlmbprn84">
              <span className="block" data-id="element-171" data-mp="f5myotmnz3q1">{slide.titleLines[0]}</span>
              <span className="block text-brand-red" data-id="element-172" data-mp="b41mzblv8vhf">{slide.titleLines[1]}</span>
            </h2>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-steel-100 md:text-xl" data-id="element-173" data-mp="64ysum7fc9o8">{slide.copy}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-174" data-mp="176v3bk3ltc4">
              <ButtonLink to={slide.primary.to} size="lg" showArrow data-id="element-175" data-mp="3scorqddmggoh">
                {slide.primary.label}
              </ButtonLink>
              <ButtonLink to={slide.secondary.to} variant="outline" size="lg" data-id="element-176" data-mp="3nkx7sloz197b">
                {slide.secondary.label}
              </ButtonLink>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-charcoal-950/70" data-id="element-177" data-mp="3emv0xpik3mc4">
        <div className="mx-auto flex h-16 max-w-site items-center justify-between gap-4 px-5 md:px-8" data-id="element-178" data-mp="39lp7fph2nv1j">
          <div className="flex items-center gap-2" data-id="element-179" data-mp="34nnkuukftdlb">
            {heroSlides.map((item, i) => <button key={item.id} type="button" onClick={() => setIndex(i)} aria-label={`Show slide ${i + 1}: ${item.label}`} aria-current={i === index ? 'true' : undefined} className="group flex h-10 items-center" data-id="element-180" data-mp="2uinsx13zo2be" data-mpi={i}>
                <span className={`block h-[4px] transition-[width,background-color] duration-300 ease-out ${i === index ? 'w-14 bg-brand-red' : 'w-8 bg-white/40 group-hover:bg-white/70'}`} data-id="element-181" data-mp="39e3lt2su1s67" />
              </button>)}
            <span className="ml-3 hidden font-display text-sm font-semibold uppercase tracking-[0.16em] text-steel-300 sm:inline" data-id="element-182" data-mp="2qhlnu3ne4w1i">
              {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center gap-1" data-id="element-183" data-mp="1xzzvq7pd8o6f">
            <button type="button" onClick={() => setUserPaused(value => !value)} aria-label={userPaused ? 'Play slideshow' : 'Pause slideshow'} className="flex h-10 w-10 items-center justify-center text-white transition-colors duration-150 hover:text-brand-light" data-id="element-184" data-mp="1q0lfpz2en626">
              {userPaused ? <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-185" data-mp="1usmrljvnuuro" /> : <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-186" data-mp="171h9ey28zqe7" />}
            </button>
            <button type="button" onClick={() => go(-1)} aria-label="Previous slide" className="flex h-10 w-10 items-center justify-center border border-white/30 text-white transition-colors duration-150 hover:border-brand-action hover:bg-brand-action" data-id="element-187" data-mp="122nv53f4zvcz">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-188" data-mp="2sekb9kls1657" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next slide" className="flex h-10 w-10 items-center justify-center border border-white/30 text-white transition-colors duration-150 hover:border-brand-action hover:bg-brand-action" data-id="element-189" data-mp="2ng9096jqwple">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-190" data-mp="2lzlfiin3ex80" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}